/**
 * Display text for per-candidate contact rollups (the "Contacted" chips on
 * result cards and the experience drawer). Pure; dates pre-formatted where
 * rows are BUILT (ui-best-practices §7 / NG0100). Status is conveyed in TEXT,
 * never color alone.
 */
import type {
    MapsContactCandidateStatus,
    MapsContactDeliveryMode,
    MapsContactHistoryRow,
    MapsContactMessageStatus,
} from '../models/maps.types';
import { FormatDisplayDate } from './dates';

/** Engagement suffix per rollup status; '' when there is nothing beyond the
 *  send itself worth saying (Pending/Sent). */
const STATUS_SUFFIX: Readonly<Record<MapsContactMessageStatus, string>> = {
    Pending: '',
    Sent: '',
    Delivered: ' · Delivered',
    Opened: ' · Opened',
    Clicked: ' · Clicked',
    Bounced: ' · Bounced',
    Dropped: ' · Not delivered',
    Spam: ' · Marked as spam',
    Failed: ' · Failed',
};

/** True when the rollup represents a delivery failure — chips render these in
 *  the error style (plus the textual suffix above). */
export function IsContactStatusFailure(status: MapsContactMessageStatus): boolean {
    return status === 'Bounced' || status === 'Dropped' || status === 'Spam' || status === 'Failed';
}

/** e.g. "Contacted Aug 21, 2026 · Opened" — null when the rollup is unusable. */
export function FormatContactStatusChip(status: MapsContactCandidateStatus): string | null {
    const date = FormatDisplayDate(status.lastContactedAt);
    if (date === null) return null;
    return `Contacted ${date}${STATUS_SUFFIX[status.lastStatus] ?? ''}`;
}

// ---------------------------------------------------------- history grouping

/** One send (= one modal submit) in the history drawer, with its recipients. */
export interface ContactHistoryBatchView {
    batchId: string;
    subject: string;
    /** Pre-formatted send date (never a method call in the template — NG0100). */
    dateText: string | null;
    deliveryMode: MapsContactDeliveryMode;
    /** Non-live sends are badged as test deliveries. */
    isTest: boolean;
    recipients: ContactHistoryRecipientView[];
}

export interface ContactHistoryRecipientView {
    name: string;
    /** Textual status + engagement counts + failure reason (never color-only). */
    statusText: string;
    isFailure: boolean;
}

/**
 * Group the server's flat, newest-first history rows into batches (one per
 * modal send), preserving the newest-first order of first appearance. Pure —
 * spec'd in contact-status.spec.ts.
 */
export function GroupContactHistory(rows: readonly MapsContactHistoryRow[]): ContactHistoryBatchView[] {
    const batches = new Map<string, ContactHistoryBatchView>();
    for (const row of rows) {
        let batch = batches.get(row.batchId);
        if (!batch) {
            batch = {
                batchId: row.batchId,
                subject: row.subject,
                dateText: FormatDisplayDate(row.createdAt),
                deliveryMode: row.deliveryMode,
                isTest: row.deliveryMode !== 'live',
                recipients: [],
            };
            batches.set(row.batchId, batch);
        }
        batch.recipients.push({
            name: row.recipientName ?? row.recipientEmail,
            statusText: describeHistoryStatus(row),
            isFailure: IsContactStatusFailure(row.status),
        });
    }
    return [...batches.values()];
}

function describeHistoryStatus(row: MapsContactHistoryRow): string {
    const parts: string[] = [row.status];
    if (row.openCount > 0) parts.push(`${row.openCount} open${row.openCount === 1 ? '' : 's'}`);
    if (row.clickCount > 0) parts.push(`${row.clickCount} click${row.clickCount === 1 ? '' : 's'}`);
    if (row.bounceReason) parts.push(row.bounceReason);
    return parts.join(' · ');
}
